<template>
  <div
    class="tw:flex tw:flex-col tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:bg-gray-50 tw:p-6"
    :data-testid="testidPrefix">
    <div class="tw:flex tw:items-start tw:justify-between tw:gap-6">
      <div class="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
        <span v-if="title" class="tw:text-14 tw:font-semibold tw:text-gray-900">{{ title }}</span>

        <span class="tw:text-13 tw:text-gray-600" :data-testid="`${testidPrefix}-number`">
          <PhoneNumberForm
            v-if="editing"
            v-model="numberDraft"
            :testid-prefix="testidPrefix"
            :error="fieldError"
            :loading="isSending"
            :cancellable="hasNumber"
            @save="saveNumber"
            @cancel="cancelChange" />

          <template v-else-if="hasNumber">
            <template v-if="awaitingCode && editable"
              >{{ $t('registration.profile.mobile_sent_to') }}{{ ' ' }}</template
            >
            <strong class="tw:font-medium">{{ displayNumber }}</strong>
            <template v-if="editable">
              <span class="tw:mx-1 tw:text-muted">&middot;</span>
              <TextLinkButton :data-testid="`${testidPrefix}-change`" @click="startChange">
                {{ $t('registration.profile.mobile_change') }}
              </TextLinkButton>
            </template>
          </template>

          <template v-else>{{ $t('account.profile.phone.none') }}</template>
        </span>
      </div>

      <LeadPill
        v-if="hasNumber && !editing"
        :tone="awaitingCode ? 'neutral' : 'success'"
        role="status"
        :data-testid="`${testidPrefix}-status`">
        <IconCheck v-if="!awaitingCode" :size="14" />
        {{ awaitingCode ? $t('registration.profile.mobile_pending') : $t('registration.profile.mobile_verified') }}
      </LeadPill>
    </div>

    <span
      v-if="notice && !editing && !awaitingCode"
      class="tw:text-13 tw:text-gray-600"
      role="status"
      :data-testid="`${testidPrefix}-notice`">
      {{ notice }}
    </span>

    <template v-if="awaitingCode && editable && !editing">
      <!-- the boxes stay visible; only the action text changes from "Send" to "Resend" -->
      <CodeInput
        v-model="code"
        size="md"
        :show-paste-hint="false"
        :has-error="Boolean(shownError)"
        :disabled="isVerifying"
        :aria-label="$t('registration.profile.code_label')"
        :data-testid="`${testidPrefix}-code`"
        @complete="verifyCode" />

      <span v-if="shownError" class="tw:text-13 tw:text-danger" role="alert" :data-testid="`${testidPrefix}-error`">
        {{ shownError }}
      </span>

      <PhoneCodeActions
        ref="actionsRef"
        :testid-prefix="testidPrefix"
        :code-sent="codeSent"
        :cooldown-active="cooldownActive"
        :resend-after-seconds="resendAfterSeconds"
        :is-sending="isSending"
        :is-calling="isCalling"
        @send="(channel) => sendCode(channel)"
        @cooldown-finished="cooldownActive = false">
        <template v-if="pendingNumber !== undefined">
          <span class="tw:mx-1 tw:text-muted">&middot;</span>
          <TextLinkButton :data-testid="`${testidPrefix}-discard`" @click="discardPending">
            {{ $t('registration.profile.mobile_cancel') }}
          </TextLinkButton>
        </template>
      </PhoneCodeActions>
    </template>
  </div>
</template>

<script setup lang="ts">
/** The code flow for confirming a phone number. The caller supplies the requests, so registration and the account page share it. */
import { IconCheck } from '@tabler/icons-vue';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

import PhoneCodeActions from '@/components/generic/PhoneCodeActions.vue';
import PhoneNumberForm from '@/components/generic/PhoneNumberForm.vue';
import TextLinkButton from '@/components/generic/TextLinkButton.vue';

import CodeInput from '@/components/registration/subcomponents/CodeInput.vue';
import {
  readRegistrationErrorCode,
  registrationErrorMessage,
  verificationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';
import LeadPill from '@/components/generic/LeadPill.vue';
import type { RegistrationErrorCode } from '@/types/registration';
import type { PhoneCodeChannel, PhoneSendRequest, PhoneSendResult, PhoneVerifyRequest } from '@/types/types';

interface Props {
  testidPrefix: string;
  title?: string;
  /** the number on file, already masked */
  maskedNumber?: string | null;
  verified: boolean;
  /** false shows the number and its status read-only */
  editable?: boolean;
  /** pre-fills the number field when changing */
  initialNumber?: string;
  /** a message from the caller, shown with the code boxes */
  error?: string;
  send: (request: PhoneSendRequest) => Promise<PhoneSendResult>;
  verify: (request: PhoneVerifyRequest) => Promise<boolean>;
}

const CODE_LENGTH = 6;
const FALLBACK_COOLDOWN_SECONDS = 60;

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  maskedNumber: null,
  editable: true,
  initialNumber: '',
  error: '',
});

const emit = defineEmits<{
  verified: [];
  /** a code was sent to a new number, which is not confirmed yet */
  numberChanged: [];
}>();

/* ----------------------------------- STATE ---------------------------------- */
const changeRequested = ref(false);
const numberDraft = ref('');
const code = ref('');
const codeSent = ref(false);
const cooldownActive = ref(false);
const codeError = ref('');
/** the caller's message is hidden once the user starts typing a code */
const externalDismissed = ref(false);
const fieldError = ref('');
/** an informational message shown with the confirmed number */
const notice = ref('');
const isSending = ref(false);
const isCalling = ref(false);
const isVerifying = ref(false);
const resendAfterSeconds = ref(0);
/** the mask of the number a code was last sent to */
const maskFromSend = ref('');
/** the new number a code was sent to; undefined while the number on file is the one being confirmed */
const pendingNumber = ref<string | undefined>(undefined);

const actionsRef = useTemplateRef<InstanceType<typeof PhoneCodeActions>>('actionsRef');

/* --------------------------------- COMPUTED --------------------------------- */
const shownError = computed(() => codeError.value || (externalDismissed.value ? '' : props.error));

const displayNumber = computed(() => maskFromSend.value || props.maskedNumber || '');

const hasNumber = computed(() => displayNumber.value !== '');

/** the number field also stands in for the card when there is no number to confirm */
const editing = computed(() => props.editable && (changeRequested.value || !hasNumber.value));

/** a code is expected: either the number was never confirmed or a new one is waiting for it */
const awaitingCode = computed(() => !props.verified || pendingNumber.value !== undefined);

/* --------------------------------- METHODS ---------------------------------- */
function messageFor(errorCode: RegistrationErrorCode | undefined) {
  return verificationErrorMessage(errorCode, 'mobile');
}

function startCooldown(seconds: number) {
  resendAfterSeconds.value = seconds;
  codeSent.value = true;
  cooldownActive.value = true;
  nextTick(() => actionsRef.value?.restart(seconds));
}

/** resolves true when a code is on its way to the number */
async function sendCode(channel: PhoneCodeChannel, phone = pendingNumber.value): Promise<boolean> {
  if (isSending.value || isCalling.value) return false;

  const busy = channel === 'call' ? isCalling : isSending;
  busy.value = true;
  codeError.value = '';
  fieldError.value = '';

  try {
    const result = await props.send({ phone, channel });
    maskFromSend.value = result.maskedNumber ?? maskFromSend.value;
    if (phone !== undefined) pendingNumber.value = phone;
    code.value = '';
    startCooldown(result.resendAfterSeconds);

    return true;
  } catch (caught) {
    const reason = readRegistrationErrorCode(caught);
    // a number that cannot be used is a field error; a code already in flight isn't a failure, so the countdown just restarts
    if (reason === 'verification_invalid_phone' || reason === 'verification_delivery_unavailable') {
      if (!editing.value) startChange();
      fieldError.value = registrationErrorMessage(caught, messageFor);
    } else if (reason === 'verification_already_verified') {
      // the number on file is already confirmed, so no code was sent
      discardPending();
      cancelChange();
      notice.value = registrationErrorMessage(caught, messageFor);
    } else if (reason === 'verification_throttled') {
      // a code is already on its way to this number
      if (phone !== undefined) pendingNumber.value = phone;
      startCooldown(FALLBACK_COOLDOWN_SECONDS);

      return true;
    } else if (editing.value) {
      fieldError.value = registrationErrorMessage(caught, messageFor);
    } else {
      codeError.value = registrationErrorMessage(caught, messageFor);
    }

    return false;
  } finally {
    busy.value = false;
  }
}

async function verifyCode() {
  if (code.value.length !== CODE_LENGTH || isVerifying.value) return;

  isVerifying.value = true;
  codeError.value = '';

  let confirmed = false;
  try {
    confirmed = await props.verify({ code: code.value, phone: pendingNumber.value });
    if (!confirmed) codeError.value = messageFor('verification_code_incorrect');
  } catch (caught) {
    codeError.value = registrationErrorMessage(caught, messageFor);
  } finally {
    isVerifying.value = false;
  }

  code.value = '';
  if (!confirmed) return;

  pendingNumber.value = undefined;
  emit('verified');
}

function startChange() {
  changeRequested.value = true;
  notice.value = '';
  numberDraft.value = pendingNumber.value ?? props.initialNumber;
}

/** drops a new number that is waiting for its code, back to the one on file */
function discardPending() {
  pendingNumber.value = undefined;
  maskFromSend.value = '';
  codeSent.value = false;
  cooldownActive.value = false;
  codeError.value = '';
  code.value = '';
}

function cancelChange() {
  changeRequested.value = false;
  numberDraft.value = '';
  fieldError.value = '';
}

/** saving a new number sends the code to it straight away */
async function saveNumber() {
  const next = numberDraft.value.trim();
  if (!next) {
    fieldError.value = messageFor('verification_invalid_phone');
    return;
  }

  if (!(await sendCode('sms', next))) return;

  changeRequested.value = false;
  emit('numberChanged');
}

/* --------------------------------- WATCHERS --------------------------------- */
watch(code, (value) => {
  // clear as soon as the user starts correcting
  if (value.length === 0) return;
  codeError.value = '';
  externalDismissed.value = true;
});

// once the caller shows the confirmed number, the mask from the send is no longer needed
watch(
  () => props.maskedNumber,
  () => {
    maskFromSend.value = '';
  }
);

watch(
  () => props.error,
  () => {
    externalDismissed.value = false;
  }
);
</script>
